import { computed } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  FieldMetadata,
  FindContractsQuery,
  FilterMode,
  FilterType,
  GlobalTenantContractsApi,
  MetadataModel,
  SortDirection,
  TenantContractModel,
  TenantContractsApi,
} from 'src/app/usage-api';
import { AuthService } from '../../authentication/auth.service';
import { BaseDataSource, TableColumn } from './datasource';
import { MetadataFilterManager } from './metadata-filter.service';
import { BehaviorSubject } from 'rxjs';
import { formatDateTime } from '../../pipes/fix-date/fix-date.pipe';
import { LocaleHelper } from 'src/app/l10n-config';

export interface FindContractsRequest {
  page?: number;
  pageSize?: number;
  query?: FindContractsQuery;
}

const ERROR_MESSAGE_KEY = 'BackOffice.ManageContracts.Table.ErrorMessage';

export class ContractsDataSource extends BaseDataSource<
  TenantContractModel,
  GlobalTenantContractsApi,
  FindContractsRequest
> {
  protected columns: TableColumn<TenantContractModel>[] = [];
  private readonly metadataManager: MetadataFilterManager<TenantContractModel>;
  private readonly defaultTenantId: string;
  private readonly onMetadataLoaded?: (metadata: MetadataModel) => void;

  readonly metadata = computed(() => this.metadataManager.metadata());
  readonly isMetadataLoaded = computed(() => this.metadataManager.isMetadataLoaded());
  readonly searchFilters = computed(() => this.metadataManager.searchFilters());
  readonly metadataColumnsChanged = computed(() => this.metadataManager.columnsChanged());

  notifyColumnsChanged(): void {
    this.columnsChanged.next(true);
  }

  get availableColumns(): TableColumn<TenantContractModel>[] {
    return this.metadataManager.getColumns().filter((f) => this.authService.hasAllPermissions(f.permissions ?? []));
  }

  get queryArguments(): object {
    const sortOrder = this.currentRequest.value.query?.sort?.[0]?.order === 'Ascending' ? '' : '-';

    return {
      page: this.currentRequest.value.page,
      pageSize: this.currentRequest.value.pageSize,
      sort: sortOrder + (this.currentRequest.value.query?.sort?.[0]?.field ?? ''),
      isActive: this.filters.isActive$.value !== undefined ? this.filters.isActive$.value : undefined,
      ...this.metadataManager.queryFriendlyFilters(this.currentRequest.value.query?.filters),
    };
  }

  filters = {
    isActive$: new BehaviorSubject<boolean | undefined>(undefined),
  };

  constructor(
    protected service: GlobalTenantContractsApi,
    protected tenantService: TenantContractsApi,
    protected authService: AuthService,
    paginator: MatPaginator,
    sort: MatSort,
    request: FindContractsRequest,
    loadOnInit: boolean = true,
    onMetadataLoaded?: (metadata: MetadataModel) => void,
  ) {
    super(service, paginator, sort, request, loadOnInit);
    this.defaultTenantId = authService.user?.snapshot?.tenantId?.toString() ?? '';
    this.onMetadataLoaded = onMetadataLoaded;

    this.metadataManager = new MetadataFilterManager<TenantContractModel>({
      loadMetadata: () => this.tenantService.tenantContractsGetMetadata(this.defaultTenantId),
      getLocalizationKey: this.getLocalizationKey.bind(this),
      customizeColumn: this.customizeColumn.bind(this),
      dateRangeFields: ['contractStartDate', 'servicePeriodEndDate'],
      onSearchFiltersChange: () => {
        if (this.hasInit) this.loadDataTerm$.next(true);
      },
      onColumnsChanged: () => this.columnsChanged.next(true),
    });

    this.loadingSubject.next(true);
    this.metadataManager.loadMetadata({
      subscription: (sub) => this.subscriptions.add(sub),
      next: (metadata) => {
        this.onMetadataLoaded?.(metadata);
        if (this.hasInit) {
          this.loadDataTerm$.next(true);
        }
      },
      error: (err: unknown) => {
        console.error('Failed to load contracts metadata:', err);
        this.hasError.next(true);
        this.errorMessage.next(ERROR_MESSAGE_KEY);
        this.loadingSubject.next(false);
      },
    });
  }

  override init(setInit: boolean = false) {
    super.init(setInit);

    this.subscriptions.add(this.createSubscription(this.filters.isActive$, true, true));

    this.hasInit = true;

    // If metadata is already loaded, trigger data loading
    if (this.metadataManager.isMetadataLoaded()) {
      this.loadDataTerm$.next(true);
    }
  }

  public updateFilter(
    field: string,
    values: string[] | null,
    mode: FilterMode = FilterMode.Includes,
    type: FilterType = FilterType.Values,
  ): void {
    this.metadataManager.updateFilter(field, values, mode, type);
  }

  public loadData(forceRefresh: boolean): void {
    if (!this.metadataManager.metadata()?.fields?.length) {
      if (!this.metadataManager.isMetadataLoaded() && !this.metadataManager.isLoading()) {
        this.loadingSubject.next(true);
        this.hasError.next(false);
        this.errorMessage.next(null);
        this.metadataManager.loadMetadata({
          subscription: (sub) => this.subscriptions.add(sub),
          next: (_) => this.loadData(true),
          error: (err: unknown) => {
            console.error('Failed to load contracts metadata:', err);
            this.hasError.next(true);
            this.errorMessage.next(ERROR_MESSAGE_KEY);
            this.loadingSubject.next(false);
          },
        });
      }
      return;
    }
    this.loadDataFromConfig({
      forceRefresh: forceRefresh,
      errorMessage: ERROR_MESSAGE_KEY,
      requestTransformer: () => {
        const request: FindContractsRequest & { active?: boolean } = {
          page: this.pageIndex + 1,
          pageSize: this.pageSize,
          query: {
            sort: this.sortField
              ? [
                {
                  field: this.sortField,
                  order: (this.sort.direction === 'desc' ? 'Descending' : 'Ascending') as unknown as SortDirection,
                },
              ]
              : null,
            filters: this.metadataManager.getSearchFilters(),
          },
          active: this.filters.isActive$.value ?? undefined,
        };

        return request;
      },

      request: (request: FindContractsRequest & { active?: boolean }) =>
        this.service.globalTenantContractsFilterContracts(request.active, request.page, request.pageSize, request.query),

      isEqual: (previous: FindContractsRequest & { active?: boolean }, current: FindContractsRequest & { active?: boolean }) =>
        previous.page === current.page &&
        previous.pageSize === current.pageSize &&
        previous.query?.sort?.[0]?.field === current.query?.sort?.[0]?.field &&
        previous.query?.sort?.[0]?.order === current.query?.sort?.[0]?.order &&
        previous.active === current.active &&
        this.metadataManager.areFiltersEqual(previous.query?.filters, current.query?.filters),
    });
  }

  destroy(): void {
    this.metadataManager.destroy();
  }

  private getLocalizationKey(field: FieldMetadata): string {
    const fieldKey = field.key || '';

    const localizationMap: Record<string, string> = {
      tenantName: 'BackOffice.ManageContracts.Contract.TenantName',
      tenantId: 'BackOffice.ManageContracts.Contract.TenantId',
      brand: 'BackOffice.ManageContracts.Contract.Brand',
      contractStartDate: 'BackOffice.ManageContracts.Contract.ContractStartDate',
      servicePeriodEndDate: 'BackOffice.ManageContracts.Contract.ServiceEndDate',
      isActive: 'BackOffice.ManageContracts.Contract.Active',
    };

    return localizationMap[fieldKey] || field.displayName || fieldKey;
  }

  private customizeColumn(column: TableColumn<TenantContractModel>, field: FieldMetadata): void {
    const fieldKey = field.key || '';

    switch (fieldKey) {
      case 'tenantName':
        column.link = (val: TenantContractModel) => {
          return {
            route: ['/back-office/contracts', val.tenantId, val.id],
            queryParams: null,
          };
        };
        column.sticky = true;
        break;
      case 'isActive':
        column.render = (val: TenantContractModel) => {
          return val.isActive ? 'Active' : 'Inactive';
        };
        break;
      case 'contractStartDate':
        column.render = (val: TenantContractModel) => {
          if (!val.contractStartDate) return null;
          try {
            return formatDateTime(val.contractStartDate as unknown as Date, LocaleHelper.defaultLocale, false, 'mediumDate', null);
          } catch {
            return val.contractStartDate;
          }
        };
        break;
      case 'servicePeriodEndDate':
        column.render = (val: TenantContractModel) => {
          if (!val.servicePeriodEndDate) return null;
          try {
            return formatDateTime(val.servicePeriodEndDate as unknown as Date, LocaleHelper.defaultLocale, false, 'mediumDate', null);
          } catch {
            return val.servicePeriodEndDate;
          }
        };
        break;
      default:
        return
    }
  }
}
